import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { useDatabaseData, usePage } from "@/components/page/hooks"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Progress } from "@/components/ui/progress"
import { ContactEvaluationInfo } from "@/components/whatsapp-chat/contactevaluationInfo"
import { ChatMessage, WhatsAppChat } from "@/components/whatsapp-chat/whatsappChat"
import { app } from "@/lib/app"
import { supabase } from "@/lib/data"
import { add } from "date-fns"
import { FlaskConical, RefreshCcw } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { ulid } from "ulid"
import { syntheticConversationPrompt } from "./convogen"
import { conversation1 } from "./data"
import { evaluate, generateSyntheticConversation, updateContact } from "@/lib/agents/evals/evals"

export const simulation = {
  title: "Simulations",
  description: "Manage your organization's simulations.",
  route: "/evaluations/simulation",
  url: "/evaluations/simulation",
  icon: FlaskConical,
  component,
  group: "dev",
  resource: "simulations",
  action: "read",
} satisfies PageConfig

const simluatedAgent: Contact = {
  id: ulid(),
  type: "ai",
  name: "[Simulated]",
}

const WINDOW_SIZE = 10


function component() {

  const { navigate, team, } = usePage()

  const { data: eval_items, loading: eval_items_loading } = useDatabaseData("eval_items")
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  const [loadingContacts, setLoadingContacts] = useState(true)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const virtualContacts = useRef({} as { [id: string]: Contact })
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [generatePrompt, setGeneratePrompt] = useState(syntheticConversationPrompt)
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false)
  const [generatingOpen, setGeneratingOpen] = useState(false)
  const [progressOpen, setProgressOpen] = useState(false)
  const [progressCurrent, setProgressCurrent] = useState(0)
  const [progressTotal, setProgressTotal] = useState(0)

  useEffect(() => {
    if (team) loadContacts().then(() => { })
  }, [team])

  const contact = contacts.find(c => c.id === selectedContactId)


  async function loadContacts() {
    setMessages([])
    setSelectedContactId(null)
    setLoadingContacts(true)
    let query = supabase.from("contacts").select().eq("team", team?.id).eq("type", "synthetic").order("created_at", { ascending: true })
    let { data } = await query
    data ||= []
    console.log("Contacts:", data)
    data = (data as any[]).map((c: Contact) => {
      c.evaluation ||= {}
      return c as any
    })
    setContacts(data as any)
    setLoadingContacts(false)
  }

  async function onRefreshContacts() {
    await loadContacts()
  }

  async function onContactSelected(id: string = null) {

    if (!id) {
      setSelectedContactId(null)
      setMessagesLoading(false)
      setMessages([])
      return
    }

    setMessagesLoading(true)

    const { data, error } = await supabase.from("messages").select(`*, 
      agent(
        id,
        title
      )`).eq("contact", id).order("created_at", { ascending: true })

    console.log(data)

    const newMessages: ChatMessage[] = []

    if (data && data.length > 0) {
      const vcontacts = virtualContacts.current

      for (const md of data) {
        const m: ChatMessage = { ...md } as any
        if (m.role == "assistant") {
          const agentContact = simluatedAgent
          const a: Agent = m.agent as any
          if (a) {
            id = `${a.id}`
            agentContact.id = `${a.id}`
            agentContact.name = a.title
          }
          vcontacts[agentContact.id] = agentContact
          m.contactObject = agentContact
          m.direction = "right"
        } else {
          m.contactObject = contacts.find(c => c.id === m.contact)
          m.direction = "left"
        }
        newMessages.push(m)
      }
    }
    setMessages(newMessages)
    setMessagesLoading(false)
    setSelectedContactId(id)
  }

  async function onNewContact() {
    if (selectedContactId) {
      navigate(`/contacts/${selectedContactId}`)
    } else {
      navigate('/contacts/new')
    }
  }

  async function buildTestConversations() {
    let now = new Date()
    await supabase.from("messages").delete().eq("contact", selectedContactId)
    for (const m of conversation1) {
      const nm: Message = {
        contact: selectedContactId,
        team: team.id,
        message: m.content,
        role: m.role == "user" ? "synthetic" : "assistant",
        channel: "app",
        created_at: now.toISOString(),
      }
      await supabase.from("messages").insert(nm as any)
      now = add(now, { seconds: 1 })
    }
    debugger
  }


  async function clearContactEvaluation(contact: string) {
    await supabase.from('contacts').update({
      evaluation: null,
      severity: 0,
      lasteval: null,
      summary: null,
    }).eq('id', contact)
  }

  async function runEvaluation() {

    if (!selectedContactId) return

    console.log("Evaluating...", messages)

    let contact: Contact = contacts.find(c => c.id === selectedContactId)
    if (!messages || messages.length == 0 || !selectedContactId || !contact) return

    const keys = await app.fetchAPIkeys(team.id)

    await clearContactEvaluation(selectedContactId)

    await supabase.from('messages').update({
      agent_appropriate: null,
      agent_concern_level: null,
      agent_reasoning: null,
      escalation_detected: null,
      escalation_from_level: null,
      escalation_to_level: null,
      escalation_reasoning: null,
      narrative_update: null,
      agent_detected_items: null,
      user_detected_items: null,
    }).eq('contact', selectedContactId)

    contact.evaluation = {}
    contact.severity = 0
    contact.lasteval = null
    contact.summary = null

    const { data: dbeval_items } = await supabase.from("eval_items").select("*")
    const eval_items: Evaluation_Item[] = dbeval_items as any

    const totalTurns = Math.floor(messages.length / 2)
    setProgressTotal(totalTurns)
    setProgressCurrent(0)
    setProgressOpen(true)

    let counter = 1

    for (let i = 0; i < messages.length - 1; i += 2) {
      const userMessage = messages[i]
      const agentMessage = messages[i + 1]

      if (!userMessage || !agentMessage) break
      if (userMessage.role !== "synthetic" || agentMessage.role !== "assistant") continue

      const recentMessages = messages.slice(Math.max(0, i - WINDOW_SIZE), i)

      try {
        const result = await evaluate({
          userMessage: userMessage.message,
          agentResponse: agentMessage.message,
          recentMessages: recentMessages as any,
          contact,
          evalItems: eval_items as any,
          keys,
          agentSummary: "",
        })

        contact = updateContact(contact, result, eval_items)

        await supabase.from("messages").update({
          user_detected_items: result.detectedItems || null,
          escalation_from_level: result.escalation?.fromLevel || null,
          escalation_to_level: result.escalation?.toLevel || null,
          escalation_reasoning: result.escalation?.reasoning || null,
        }).eq('id', userMessage.id)

        await supabase.from("messages").update({
          agent_appropriate: result.agentEvaluation?.appropriate || false,
          agent_concern_level: result.agentEvaluation?.concernLevel || null,
          agent_reasoning: result.agentEvaluation?.reasoning || null,
          narrative_update: (result.narrativeUpdate?.shouldUpdate && result.narrativeUpdate?.newSummary) ? result.narrativeUpdate?.newSummary : null,
          agent_detected_items: result.agentEvaluation?.detectedItems || null,
        }).eq('id', agentMessage.id)
        setProgressCurrent(counter)
        console.log(`Items ${counter++} of ${messages.length / 2}`)

      } catch (err) {
        console.error(`  Error on turn ${Math.floor(i / 2) + 1}:`, err)
      }
    }

    await supabase.from('contacts').update({
      evaluation: contact.evaluation as any || null,
      severity: contact.severity || null,
      lasteval: new Date().toISOString(),
      summary: contact.summary || null,
    }).eq('id', selectedContactId)

    onContactSelected(selectedContactId)
    setProgressOpen(false)

    console.log(`\n--- Final Contact State ---`)
    console.log(`Global Severity: ${contact.severity}`)
    console.log(`Narrative: ${contact.summary || "none"}`)
    console.log(`Profile:`, JSON.stringify(contact.evaluation, null, 2))

    const allContacts = contacts
    setContacts(allContacts.map(c => c.id === selectedContactId ? contact : c))

  }

  function evaluateChat() {
    setConfirmOpen(true)
  }

  async function generateConvo() {
    console.log("Generate")

    if (!selectedContactId) return
    const apikey = await app.fetchAPIkeys(team.id)

    await clearContactEvaluation(selectedContactId)
    await supabase.from("messages").delete().eq("contact", selectedContactId)


    setGeneratingOpen(true)
    const gen = await generateSyntheticConversation(generatePrompt, apikey.openai)

    let now = new Date()
    const allMessages: Message[] = []

    for (const m of gen) {
      const nm: Message = {
        contact: selectedContactId,
        team: team.id,
        message: m.content,
        role: m.role == "user" ? "synthetic" : "assistant",
        channel: "app",
        created_at: now.toISOString(),
      }
      allMessages.push(nm)
      now = add(now, { seconds: 1 })
    }

    await supabase.from("messages").insert(allMessages as any)

    setGeneratingOpen(false)
    onContactSelected(selectedContactId)
    console.log("Done")

  }

  async function onGenerateConvo() {
    setGeneratePrompt(syntheticConversationPrompt)
    setGenerateOpen(true)
  }

  if (eval_items_loading) return <CRUDLoader />

  const items: Record<string, Evaluation_Item> = {}
  for (const item of eval_items) {
    items[item.id] = item as any
  }


  return <Page>
    <div className="h-full grid grid-rows-[auto_1fr] gap-4">
      <div className="flex">
        <PageTitle />
        <div className="grow"></div>
        <div className="flex items-center gap-4">
          <div>
            <Button className="rounded-lg" onClick={onNewContact}>{selectedContactId ? "Edit Contact" : "New Contact"}</Button>
          </div>
          <div title="Refresh">
            <RefreshCcw className="h-8 w-8 text-muted-foreground cursor-pointer" onClick={onRefreshContacts} />
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0">

        <WhatsAppChat
          className="h-full"
          contacts={contacts}
          messages={messages}
          selectedContactId={selectedContactId}
          onContactSelected={onContactSelected}
          loadingContacts={loadingContacts}
          loadingMessages={messagesLoading}
          evaluationItems={items}
          hideContactName
        >

          <WhatsAppChat.Toolbar>
            <div className="flex w-full items-center pb-1 ">
              <div className="">{contacts.length} Contacts</div>
              <div className="grow" />
            </div>
          </WhatsAppChat.Toolbar>

          <WhatsAppChat.ContactInfo>
            <ContactEvaluationInfo contact={contact} items={items} />
          </WhatsAppChat.ContactInfo>


          <WhatsAppChat.MessageInput>
            <div className="py-4 pl-4 flex gap-2">
              <Button onClick={evaluateChat}>Evaluate Conversation</Button>
              <Button onClick={onGenerateConvo}>Generate Conversation</Button>
            </div>
          </WhatsAppChat.MessageInput>

        </WhatsAppChat>
      </div>

    </div>

    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Run Evaluation</AlertDialogTitle>
          <AlertDialogDescription>
            This will delete all existing evaluation data for this contact and re-evaluate the conversation. Are you sure you want to continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setConfirmOpen(false); runEvaluation() }}>Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
      <DialogContent className="sm:max-w-3xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Generate Conversation</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 flex-1 min-h-0">
          <p className="text-sm text-muted-foreground">Edit the prompt below to customize the synthetic conversation generation.</p>
          <textarea
            className="flex-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono resize-none focus:outline-hidden focus:ring-2 focus:ring-ring"
            value={generatePrompt}
            onChange={(e) => setGeneratePrompt(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setGenerateOpen(false)}>Cancel</Button>
          <Button onClick={() => { setGenerateOpen(false); setGenerateConfirmOpen(true) }}>Accept</Button>
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={generateConfirmOpen} onOpenChange={setGenerateConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Generate Conversation</AlertDialogTitle>
          <AlertDialogDescription>
            This will delete all previous conversation data and generate a new synthetic conversation. Are you sure you want to continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setGenerateConfirmOpen(false); generateConvo() }}>Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <Dialog open={generatingOpen} onOpenChange={() => { }}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Generating Conversation</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <p className="text-sm text-muted-foreground">Generating synthetic conversation, please wait...</p>
          <Progress value={null} className="animate-pulse" />
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={progressOpen} onOpenChange={() => { }}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Evaluating Conversation</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <p className="text-sm text-muted-foreground">Processing turn {progressCurrent} of {progressTotal}</p>
          <Progress value={progressTotal > 0 ? (progressCurrent / progressTotal) * 100 : 0} />
        </div>
      </DialogContent>
    </Dialog>

  </Page>

}


